import { batch } from 'react-redux';
import { toast } from 'react-toastify';

import { useAppDispatch } from 'redux/store/store.types';
import { ApiPaginationResponse } from 'types';
import { getApiErrorMessage } from 'utils';

import { FetchGoCertificatesShortenPayload } from 'modules/eecs-go/eecs-certificates';
import {
  FetchProductionDevicesPayload,
  useLazyFetchProductionDevicesQuery,
} from 'modules/eecs-go/eecs-production-device';

import {
  useAddForwardMutation,
  useAssignBundleMutation,
  useDeleteForwardMutation,
  useLazyCloneForwardQuery,
  useLazyDeleteAssignmentQuery,
  useLazyFetchAssignBundlesQuery,
  useLazyFetchForwardsQuery,
  useLazyFetchShortenForwardsQuery,
  useUpdateForwardModeMutation,
  useUpdateForwardMutation,
} from '../api/eecs-forwards.api';
import { EecsForwardAC, setForwardModal, setSelectedForward } from '../store/eecs-forwards.slice';
import {
  AddForwardPayload,
  CloneForwardPayload,
  DeleteForwardAssignmentPayload,
  DeleteForwardPayload,
  FetchForwardAssignBundlesPayload,
  FetchForwardsPayload,
  ForwardAssignBundlePayload,
  ForwardAssignBundlesResponse,
  IForward,
  IForwardModal,
  IForwardShorten,
  UpdateForwardModePayload,
  UpdateForwardPayload,
} from '../types/eecs-forwards.types';

interface OpenModal {
  modal: IForwardModal;
  forward: IForward | null;
}

interface UseEecsForward {
  clearForwardState: () => void;
  openForwardsModal: (payload: OpenModal) => void;
  closeForwardModal: () => void;
  fetchProductionDevicesOptionsHandler: (
    payload: FetchProductionDevicesPayload,
  ) => Promise<Array<{ id: number | string; name: string }>>;
  fetchShortenForwardsHandler: (
    payload: FetchGoCertificatesShortenPayload,
  ) => Promise<ApiPaginationResponse<IForwardShorten[]> | null>;

  fetchForwardsHandler: (payload: FetchForwardsPayload) => Promise<boolean>;
  addForwardHandler: (payload: AddForwardPayload) => Promise<IForward | null>;
  updateForwardHandler: (payload: UpdateForwardPayload) => Promise<IForward | null>;
  deleteForwardHandler: (payload: DeleteForwardPayload) => Promise<boolean>;
  cloneForwardHandler: (payload: CloneForwardPayload) => Promise<IForward | null>;

  fetchAssignBundlesHandler: (payload: FetchForwardAssignBundlesPayload) => Promise<ForwardAssignBundlesResponse[]>;
  assignBundleHandler: (payload: ForwardAssignBundlePayload) => Promise<boolean>;
  deleteAssignmentHandler: (payload: DeleteForwardAssignmentPayload) => Promise<boolean>;
  updateForwardModeHandler: (payload: UpdateForwardModePayload) => Promise<boolean>;
}

export const useEecsForward = (): UseEecsForward => {
  const dispatch = useAppDispatch();

  const [fetchProductionDevicesQuery] = useLazyFetchProductionDevicesQuery();
  const [fetchForwardsQuery] = useLazyFetchForwardsQuery();
  const [addForwardQuery] = useAddForwardMutation();
  const [updateForwardQuery] = useUpdateForwardMutation();
  const [deleteForwardQuery] = useDeleteForwardMutation();
  const [cloneForwardQuery] = useLazyCloneForwardQuery();

  const [fetchAssignBundlesQuery] = useLazyFetchAssignBundlesQuery();
  const [assignBundleQuery] = useAssignBundleMutation();
  const [deleteAssignmentQuery] = useLazyDeleteAssignmentQuery();
  const [updateForwardModeQuery] = useUpdateForwardModeMutation();
  const [fetchShortenForwardsQuery] = useLazyFetchShortenForwardsQuery();

  const fetchShortenForwardsHandler = async (payload: FetchGoCertificatesShortenPayload) => {
    const { data: response, error } = await fetchShortenForwardsQuery(payload);
    const isError = error || !response?.data;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to get EECS Forwards!');
      return null;
    }
    return response;
  };

  const updateForwardModeHandler = async (payload: UpdateForwardModePayload) => {
    const { data: response, error } = await updateForwardModeQuery(payload);
    const isError = error || !response?.data;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to update EECS Forward!');
    } else {
      batch(() => {
        dispatch(setSelectedForward(response.data));
        dispatch(EecsForwardAC.editItem(response.data));
      });
    }
    return !isError;
  };

  const fetchAssignBundlesHandler = async (payload: FetchForwardAssignBundlesPayload) => {
    const { data: response, error } = await fetchAssignBundlesQuery(payload);
    const isError = error || !response?.data;
    if (isError) {
      return [];
    } else {
      return response.data;
    }
  };

  const assignBundleHandler = async (payload: ForwardAssignBundlePayload) => {
    dispatch(EecsForwardAC.toggleLoading(true));
    const { data: response, error } = await assignBundleQuery(payload);
    dispatch(EecsForwardAC.toggleLoading(false));
    const isError = error || !response?.data;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to assign bundle!');
    } else {
      batch(() => {
        dispatch(setSelectedForward(response.data));
        dispatch(EecsForwardAC.editItem(response.data));
      });
    }
    return !isError;
  };

  const deleteAssignmentHandler = async (payload: DeleteForwardAssignmentPayload) => {
    dispatch(EecsForwardAC.toggleLoading(true));
    const { data: response, error } = await deleteAssignmentQuery(payload);
    dispatch(EecsForwardAC.toggleLoading(false));
    const isError = error || !response?.data;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to delete bundle!');
    } else {
      batch(() => {
        dispatch(setSelectedForward(response.data));
        dispatch(EecsForwardAC.editItem(response.data));
      });
    }
    return !isError;
  };

  const fetchForwardsHandler = async (payload: FetchForwardsPayload) => {
    dispatch(EecsForwardAC.toggleLoading(true));
    const { data: response, error } = await fetchForwardsQuery(payload);
    dispatch(EecsForwardAC.toggleLoading(false));
    const isError = error || !response?.data;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to get EECS Forwards!');
    } else {
      batch(() => {
        dispatch(EecsForwardAC.setItems(response.data));
        dispatch(EecsForwardAC.setPaginationMeta(response.meta));
      });
    }

    return !isError;
  };

  const cloneForwardHandler = async (payload: CloneForwardPayload) => {
    dispatch(EecsForwardAC.toggleLoading(true));
    const { data: response, error } = await cloneForwardQuery(payload);
    const isError = error || !response?.data?.id;
    if (isError) {
      dispatch(EecsForwardAC.toggleLoading(false));
      toast.error(getApiErrorMessage(error) || 'Failed to clone EECS Forward!');
      return null;
    } else {
      toast.success('Forward successfully cloned!');
    }

    return response.data;
  };

  const addForwardHandler = async (payload: AddForwardPayload) => {
    dispatch(EecsForwardAC.toggleLoading(true));
    const { data: response, error } = await addForwardQuery(payload);
    dispatch(EecsForwardAC.toggleLoading(false));
    const isError = error || !response?.data?.id;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to add EECS Forward!');
      return null;
    } else {
      toast.success('Forward successfully added!');
      dispatch(EecsForwardAC.addItem(response.data));
      return response.data;
    }
  };

  const updateForwardHandler = async (payload: UpdateForwardPayload) => {
    dispatch(EecsForwardAC.toggleLoading(true));
    const { data: response, error } = await updateForwardQuery(payload);
    dispatch(EecsForwardAC.toggleLoading(false));
    const isError = error || !response?.data?.id;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to update EECS Forward!');
      return null;
    } else {
      toast.success('Forward successfully updated!');
      return response.data;
    }
  };

  const deleteForwardHandler = async (payload: DeleteForwardPayload) => {
    dispatch(EecsForwardAC.toggleLoading(true));
    const { error } = await deleteForwardQuery(payload);
    dispatch(EecsForwardAC.toggleLoading(false));
    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to delete EECS Forward!');
    } else {
      toast.success('Forward successfully deleted!');
      dispatch(EecsForwardAC.removeItem(payload.id));
    }

    return !error;
  };

  const fetchProductionDevicesOptionsHandler = async (payload: FetchProductionDevicesPayload) => {
    const { data: response, error } = await fetchProductionDevicesQuery(payload);
    const isError = error || !response?.data;
    if (isError) {
      return [];
    }

    return response.data.map((item) => ({
      name: item.production_device_name,
      id: item.production_device_id,
      operational_date: item.operational_date,
    }));
  };

  const openForwardsModal = ({ modal, forward }: OpenModal) => {
    batch(() => {
      dispatch(setSelectedForward(forward));
      dispatch(setForwardModal(modal));
    });
  };

  const closeForwardModal = () => {
    batch(() => {
      dispatch(setSelectedForward(null));
      dispatch(setForwardModal(null));
    });
  };

  const clearForwardState = () => {
    batch(() => {
      dispatch(EecsForwardAC.clearItems());
      dispatch(EecsForwardAC.clearFilters());
      dispatch(EecsForwardAC.clearSorts());
      dispatch(EecsForwardAC.setCurrentPage(1));
      dispatch(setSelectedForward(null));
      dispatch(setForwardModal(null));
    });
  };

  return {
    clearForwardState,
    openForwardsModal,
    closeForwardModal,
    fetchProductionDevicesOptionsHandler,
    fetchForwardsHandler,
    addForwardHandler,
    updateForwardHandler,
    deleteForwardHandler,
    cloneForwardHandler,
    fetchAssignBundlesHandler,
    assignBundleHandler,
    deleteAssignmentHandler,
    updateForwardModeHandler,
    fetchShortenForwardsHandler,
  };
};
